/* eslint-disable react/prop-types */
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, Image as ImageIcon, ExternalLink } from "lucide-react";

/* ------------------------------------------------------------------------- */
/* SectionCard — bloc d'édition générique                                     */
/* ------------------------------------------------------------------------- */
export function SectionCard({ title, description, children, action }) {
  return (
    <Card className="p-5 space-y-3" data-testid={`section-${title?.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-semibold text-base">{title}</h2>
          {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
        </div>
        {action}
      </div>
      <div>{children}</div>
    </Card>
  );
}

/* ------------------------------------------------------------------------- */
/* FieldRow — champ texte simple avec label                                   */
/* ------------------------------------------------------------------------- */
export function FieldRow({ label, value, onChange, placeholder, type = "text", helper, testid }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <Input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid || `field-${label.toLowerCase().replace(/\s+/g, "-")}`}
      />
      {helper && <p className="text-[11px] text-muted-foreground">{helper}</p>}
    </div>
  );
}

export function TextAreaRow({ label, value, onChange, placeholder, rows = 4, helper, testid }) {
  return (
    <div className="space-y-1">
      <label className="text-xs font-medium text-muted-foreground">{label}</label>
      <textarea
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        data-testid={testid || `textarea-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
      />
      {helper && <p className="text-[11px] text-muted-foreground">{helper}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* GalleryEditor — édition d'une galerie d'images simples                     */
/* ------------------------------------------------------------------------- */
export function GalleryEditor({ items = [], onChange }) {
  const [url, setUrl] = useState("");
  const [caption, setCaption] = useState("");
  const [credit, setCredit] = useState("");
  const [year, setYear] = useState("");

  const add = () => {
    if (!url.trim()) return;
    const next = [
      ...items,
      {
        url: url.trim(),
        caption: caption.trim() || null,
        credit: credit.trim() || null,
        year: year ? parseInt(year, 10) : null,
      },
    ];
    onChange(next);
    setUrl(""); setCaption(""); setCredit(""); setYear("");
  };

  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">Aucune image pour le moment. Ajoute des URL d'images publiques (photos officielles, podiums, événements…).</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {items.map((it, idx) => (
            <div key={idx} className="group relative aspect-video rounded-md overflow-hidden border bg-muted" data-testid={`gallery-item-${idx}`}>
              <img src={it.url} alt={it.caption || ""} className="object-cover w-full h-full" loading="lazy"
                onError={(e) => { e.currentTarget.style.opacity = "0.3"; e.currentTarget.alt = "image indisponible"; }} />
              {(it.caption || it.year) && (
                <div className="absolute inset-x-0 bottom-0 bg-black/60 text-white text-[10px] px-1.5 py-1 line-clamp-2">
                  {it.caption}{it.year ? ` · ${it.year}` : ""}
                </div>
              )}
              <button
                type="button"
                onClick={() => remove(idx)}
                data-testid={`gallery-remove-${idx}`}
                className="absolute top-1 right-1 h-6 w-6 rounded-full bg-red-500/90 text-white opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                aria-label="Supprimer"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="border-dashed border-2 border-border/60 rounded-lg p-3 space-y-2 bg-muted/20">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
          <ImageIcon className="h-3.5 w-3.5" />Ajouter une image
        </div>
        <Input data-testid="gallery-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…image.jpg" />
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          <Input data-testid="gallery-caption" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Légende (optionnel)" />
          <Input data-testid="gallery-credit" value={credit} onChange={(e) => setCredit(e.target.value)} placeholder="Crédit (optionnel)" />
          <Input data-testid="gallery-year" type="number" value={year} onChange={(e) => setYear(e.target.value)} placeholder="Année" />
        </div>
        <Button size="sm" onClick={add} disabled={!url.trim()} data-testid="gallery-add">
          <Plus className="h-3.5 w-3.5 mr-1" />Ajouter
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* StringListEditor — édition d'une liste de chaînes                          */
/* ------------------------------------------------------------------------- */
export function StringListEditor({ items = [], onChange, placeholder, testid }) {
  const [val, setVal] = useState("");
  const add = () => {
    if (!val.trim()) return;
    if (items.includes(val.trim())) return;
    onChange([...items, val.trim()]);
    setVal("");
  };
  const remove = (s) => onChange(items.filter((i) => i !== s));
  return (
    <div className="space-y-2" data-testid={testid}>
      <div className="flex flex-wrap gap-1.5">
        {items.length === 0 && <span className="text-xs text-muted-foreground italic">Aucune entrée.</span>}
        {items.map((it) => (
          <Badge key={it} variant="secondary" className="gap-1.5 pr-1">
            {it}
            <button type="button" onClick={() => remove(it)} className="rounded-full h-4 w-4 inline-flex items-center justify-center hover:bg-foreground/10" aria-label="Retirer">
              <Trash2 className="h-2.5 w-2.5" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex items-center gap-1.5">
        <Input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder={placeholder} />
        <Button size="sm" variant="outline" onClick={add} disabled={!val.trim()}>
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* PublicGallery — affichage public d'une galerie                             */
/* ------------------------------------------------------------------------- */
export function PublicGallery({ items = [] }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="public-gallery">
      {items.map((it, idx) => (
        <a
          key={idx}
          href={it.url}
          target="_blank"
          rel="noreferrer"
          data-testid={`public-gallery-item-${idx}`}
          className="group relative aspect-video rounded-lg overflow-hidden border bg-muted hover:ring-2 hover:ring-[hsl(var(--aito-lagoon))] transition-all"
        >
          <img src={it.url} alt={it.caption || ""} className="object-cover w-full h-full transition-transform duration-300 group-hover:scale-105" loading="lazy"
            onError={(e) => { e.currentTarget.style.opacity = "0.3"; }} />
          {(it.caption || it.year) && (
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent text-white text-xs px-2.5 py-2">
              <div className="font-medium line-clamp-2">{it.caption}</div>
              <div className="text-[10px] opacity-80 flex items-center justify-between mt-0.5">
                {it.year && <span>{it.year}</span>}
                {it.credit && <span className="italic">© {it.credit}</span>}
              </div>
            </div>
          )}
          <ExternalLink className="absolute top-2 right-2 h-3.5 w-3.5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </a>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* PublicAchievements — palmarès en lecture seule                             */
/* ------------------------------------------------------------------------- */
export function PublicAchievements({ items = [] }) {
  if (!items || items.length === 0) return null;
  const sorted = [...items].sort((a, b) => (b.year || 0) - (a.year || 0));
  return (
    <div className="space-y-2" data-testid="public-achievements">
      {sorted.map((a, idx) => (
        <div key={idx} className="flex items-start gap-3 p-2.5 rounded-md border bg-muted/30" data-testid={`achievement-${idx}`}>
          <Badge variant="outline" className="shrink-0 text-[10px] capitalize">
            {a.year || "—"}
          </Badge>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium">{a.title}</div>
            <div className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
              {a.competition && <span>{a.competition}</span>}
              {a.place && <span>· {a.place}</span>}
              {a.level && <span>· {a.level}</span>}
              {a.medal && <Badge variant="secondary" className="text-[10px] capitalize">{a.medal}</Badge>}
            </div>
            {a.personal_comment && <p className="text-[11px] italic mt-1 text-muted-foreground">{a.personal_comment}</p>}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/* SimpleAchievementEditor — édition d'un palmarès basique                    */
/* ------------------------------------------------------------------------- */
export function SimpleAchievementEditor({ items = [], onChange }) {
  const [draft, setDraft] = useState({ title: "", year: "", level: "", place: "", competition: "", medal: "", personal_comment: "" });

  const add = () => {
    if (!draft.title.trim()) return;
    onChange([...items, {
      title: draft.title.trim(),
      year: draft.year ? parseInt(draft.year, 10) : null,
      level: draft.level || null,
      place: draft.place || null,
      competition: draft.competition || null,
      medal: draft.medal || null,
      personal_comment: draft.personal_comment || null,
      result_type: "declare",
    }]);
    setDraft({ title: "", year: "", level: "", place: "", competition: "", medal: "", personal_comment: "" });
  };
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      {items.length === 0 ? (
        <p className="text-xs text-muted-foreground italic">Aucun titre. Ajoute un palmarès ci-dessous.</p>
      ) : (
        <div className="space-y-1.5">
          {items.map((a, idx) => (
            <div key={idx} className="flex items-center justify-between gap-2 p-2 rounded-md border bg-muted/30 text-sm" data-testid={`achievement-edit-${idx}`}>
              <div className="min-w-0">
                <div className="font-medium truncate">{a.title} {a.year && <span className="text-muted-foreground">· {a.year}</span>}</div>
                <div className="text-[11px] text-muted-foreground">{[a.competition, a.place, a.level, a.medal].filter(Boolean).join(" · ")}</div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => remove(idx)} data-testid={`achievement-remove-${idx}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
      <div className="border-dashed border-2 border-border/60 rounded-lg p-3 space-y-2 bg-muted/20">
        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Ajouter un titre</div>
        <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Intitulé (ex: Champion du Pacifique)" data-testid="achievement-title" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <Input type="number" value={draft.year} onChange={(e) => setDraft({ ...draft, year: e.target.value })} placeholder="Année" data-testid="achievement-year" />
          <Input value={draft.competition} onChange={(e) => setDraft({ ...draft, competition: e.target.value })} placeholder="Compétition" data-testid="achievement-competition" />
          <Input value={draft.place} onChange={(e) => setDraft({ ...draft, place: e.target.value })} placeholder="Lieu" data-testid="achievement-place" />
          <Input value={draft.medal} onChange={(e) => setDraft({ ...draft, medal: e.target.value })} placeholder="Médaille (or/argent/...)" data-testid="achievement-medal" />
        </div>
        <Input value={draft.personal_comment} onChange={(e) => setDraft({ ...draft, personal_comment: e.target.value })} placeholder="Commentaire (optionnel)" data-testid="achievement-comment" />
        <Button size="sm" onClick={add} disabled={!draft.title.trim()} data-testid="achievement-add">
          <Plus className="h-3.5 w-3.5 mr-1" />Ajouter
        </Button>
      </div>
    </div>
  );
}
